import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { EventCatalogOut, EventCatalogCreate, EventCatalogUpdate } from './analyticsTypes';

interface CatalogFormDialogProps {
  open: boolean;
  entry: EventCatalogOut | null; // null = create mode
  initialEventName?: string;     // pre-fill event_name in create mode
  onClose: () => void;
  onSave: (payload: EventCatalogCreate | EventCatalogUpdate) => Promise<void>;
}

export default function CatalogFormDialog({ open, entry, initialEventName, onClose, onSave }: CatalogFormDialogProps) {
  const isEdit = entry !== null;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const buildForm = (src: EventCatalogOut | null): EventCatalogCreate => ({
    event_name: src?.event_name ?? initialEventName ?? '',
    screen: src?.screen ?? '',
    category: src?.category ?? '',
    trigger: src?.trigger ?? '',
    instrumentation_type: src?.instrumentation_type ?? '',
    required_properties: src?.required_properties ?? '',
    optional_properties: src?.optional_properties ?? '',
    notes: src?.notes ?? '',
  });

  const [form, setForm] = useState<EventCatalogCreate>(buildForm(entry));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(buildForm(entry));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry, initialEventName]);

  const handleChange = (field: keyof EventCatalogCreate) => (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>{isEdit ? 'Edit Catalog Entry' : 'New Catalog Entry'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Event Name"
            value={form.event_name}
            onChange={handleChange('event_name')}
            disabled={isEdit}
            required
            fullWidth
            size="small"
          />
          <TextField
            label="Category"
            value={form.category}
            onChange={handleChange('category')}
            fullWidth
            size="small"
            placeholder="navigation, action, outcome, failure, gesture"
          />
          <TextField
            label="Screen"
            value={form.screen}
            onChange={handleChange('screen')}
            fullWidth
            size="small"
            placeholder="map, simulation_results, all"
          />
          <TextField
            label="Trigger"
            value={form.trigger}
            onChange={handleChange('trigger')}
            fullWidth
            size="small"
            placeholder="User clicks the save button"
          />
          <TextField
            label="Instrumentation Type"
            value={form.instrumentation_type}
            onChange={handleChange('instrumentation_type')}
            fullWidth
            size="small"
            placeholder="typed, removed"
          />
          <TextField
            label="Required Properties"
            value={form.required_properties}
            onChange={handleChange('required_properties')}
            fullWidth
            size="small"
            placeholder="map_id, layer_count"
          />
          <TextField
            label="Optional Properties"
            value={form.optional_properties}
            onChange={handleChange('optional_properties')}
            fullWidth
            size="small"
            placeholder="duration_ms"
          />
          <TextField
            label="Notes"
            value={form.notes}
            onChange={handleChange('notes')}
            fullWidth
            size="small"
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || !form.event_name.trim()}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
